import { useState, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange?: (v: boolean) => void;
  onSave?: (v: boolean) => Promise<void> | void;
  children?: ReactNode;
}

export function ToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
  onSave,
  children,
}: ToggleRowProps) {
  const [saved, setSaved] = useState(false);

  const handleToggle = async (v: boolean) => {
    onCheckedChange?.(v);
    if (onSave) {
      await onSave(v);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-border last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-foreground">{label}</p>
        <p className="text-[12px] text-muted-foreground mt-0.5">{description}</p>
        {checked && children ? (
          <div className="mt-3 animate-in fade-in slide-in-from-top-1 duration-200">{children}</div>
        ) : null}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {saved && <span className="text-[11px] font-mono text-primary">Saved</span>}
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          onClick={() => handleToggle(!checked)}
          className={cn(
            "relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors",
            checked ? "bg-primary" : "bg-muted"
          )}
        >
          <span
            className={cn(
              "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition translate-x-0.5 mt-0.5",
              checked ? "translate-x-4" : "translate-x-0.5"
            )}
          />
        </button>
      </div>
    </div>
  );
}
